import { useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';

import log, { logError } from '~/common/utils/log';
import { createFastLinkUrl } from '~/services/wallet/wallet.service.api';
import type { IPostWalletsDepositsResponse } from '~/services/wallet/interfaces/wallets.interface';

import { AEROPAY_WIDGET_ID } from '../../constants';
import { useAeropayLinkedBankAccounts } from '../../hooks/useAeropayLinkedBankAccounts';

import { useCreateAeropayDeposit } from './useCreateAeropayDeposit';
import { useCreateBankAccountLink } from './useCreateBankAccountLink';

export function useDepositWithAeropay({
  setCompletedDepositAmount,
}: {
  setCompletedDepositAmount?: (amount: number, response?: IPostWalletsDepositsResponse) => void;
}) {
  const theme = useMantineTheme();
  const router = useRouter();
  const abortControllerRef = useRef<AbortController | null>(null);

  const { t } = useTranslation('payments');
  const { refetchBankAccounts } = useAeropayLinkedBankAccounts({ enabled: true });

  const createBankAccountLinkMutation = useCreateBankAccountLink();
  const createAeropayDepositMutation = useCreateAeropayDeposit({ setCompletedDepositAmount });

  const closeWidget = useCallback(() => {
    try {
      // This handles the case where the user navigates away from the page before the widget is opened (widget is in a loading state)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Close any opened widget after navigation
      const widgetElement = document.getElementById(AEROPAY_WIDGET_ID);
      if (widgetElement && widgetElement.innerHTML.trim() !== '') {
        widgetElement.innerHTML = '';
      }
    } catch (error) {
      logError('Failed to close Aeropay widget:', error);
    }
  }, []);

  const handleRouteChangeStart = useCallback(() => {
    closeWidget();
  }, [closeWidget]);

  // Handling navigation events to close the widget
  useEffect(() => {
    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeStart);
    router.events.on('routeChangeError', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeStart);
      router.events.off('routeChangeError', handleRouteChangeStart);
      closeWidget();
    };
  }, [router, handleRouteChangeStart, closeWidget]);

  return useMutation({
    mutationFn: async ({
      depositAmount,
      depositAmountWithFees,
      bankAccountId,
      handleUpdateBankAccountValue,
      bonusId,
    }: {
      depositAmount: number;
      depositAmountWithFees: number;
      bankAccountId?: string;
      handleUpdateBankAccountValue?: (bankAccountId: string) => void;
      bonusId?: string;
    }) => {
      // if bank account id is provided, we don't need to create a fastlink url
      if (bankAccountId) {
        return createAeropayDepositMutation.mutateAsync({
          amount: depositAmountWithFees,
          bankAccountId,
          depositAmount,
          bonusId,
        });
      }

      // Abort controller to handle the case where the widget is loading and needs to be cancelled
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      try {
        const { token } = await createFastLinkUrl();

        // we need to import this dynamically because there is no window check in the library
        const { openWidget } = await import('aerosync-web-sdk');

        return await new Promise<void>((resolve, reject) => {
          // Check if cancelled before creating widget
          if (signal.aborted) {
            reject(new Error('Widget opening cancelled'));
            return;
          }

          const widget = openWidget({
            id: AEROPAY_WIDGET_ID,
            iframeTitle: 'Connect',
            environment: process.env.NEXT_PUBLIC_ENV === 'production' ? 'production' : 'sandbox', // currently we only have production or sandbox available
            consumerId: process.env.NEXT_PUBLIC_AEROPAY_CONSUMER_ID,
            token,
            zIndex: `${theme.other.zIndex.aeropayWidget}`,
            style: {
              width: '420px',
              height: '734px',
              bgColor: '#000000',
              opacity: 0.6,
            },

            // This method will be triggered when a bank is added successfully and the user clicks on "continue" button in the final AeroSync-UI page.
            onSuccess: async (event: { user_id: string; user_password: string }) => {
              log('Aeropay: onSuccess');
              closeWidget();
              try {
                const amount = depositAmountWithFees;

                const data = await createBankAccountLinkMutation.mutateAsync({
                  userId: event.user_id,
                  userPassword: event.user_password,
                });

                if (data?.bankAccountId) {
                  handleUpdateBankAccountValue(data?.bankAccountId);
                }

                await createAeropayDepositMutation.mutateAsync({
                  amount,
                  bankAccountId: data.bankAccountId,
                  depositAmount,
                  bonusId,
                });

                refetchBankAccounts();
                abortControllerRef.current = null;
                resolve();
              } catch (error) {
                abortControllerRef.current = null;
                refetchBankAccounts();
                reject(error);
              }
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (error: any) => {
              logError(error);
              showNotification({
                title: t('deposits.errors.aeropay.title'),
                message: error.message || t('deposits.errors.aeropay.general'),
                color: 'red',
                autoClose: 5000,
              });
              abortControllerRef.current = null;
              refetchBankAccounts();
              reject(new Error(error.message));
            },
            onClose: () => {
              log('Aeropay: onClose');
              abortControllerRef.current = null;
              reject(new Error('Aeropay: onClose'));
            },
            onLoad(): void {
              log('Aeropay: onLoad');
              abortControllerRef.current = null;
            },
            onEvent(event: object, type: string): void {
              log('Aeropay: onEvent', event, type);
            },
          });

          widget.launch();
        });
      } catch (error) {
        abortControllerRef.current = null;
        throw error;
      }
    },
  });
}
